import React, { useMemo, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";

import { SectionMenuItemsModel } from "../sewer-objects/section-details/SectionMenuItems";
import PageLayout from "../_layout/PageLayout";
import { InspectionReportStorageModel } from "../../services/inspection/InspectionReportStorage";
import { CurrentSectionStorageModel } from "../../services/sewer-object/CurrentSectionStorage";
import {
  ActionsSection,
  ButtonsSection,
} from "../../components/data-components/sections/DataSections";
import DownloadButton from "../../components/buttons/download-button/DownloadButton";
import SinglePagePdfViewer from "../../components/pdf-viewer/SinglePagePdfViewer";
import LoadingSpinner from "../../components/loading-spinner/LoadingSpinner";
import {
  ViewInspectionReport,
  ViewInspectionReportFromAssignedProject,
  DownloadInspectionReportFromAssignedProject,
  DownloadInspectionReport,
} from "../../services/authorization/Permissions";
import { permissionsService } from "../../services/authorization/UserPermissionsService";
import { CurrentInspectionStorageModel } from "../../services/inspection/CurrentInspectionStorage";
import useUrlParams from "../../services/url/UrlParamsService";
import { api } from "../../services/api/ApiService";
import { translationService } from "../../services/translation/TranslationService";
import { useHistory } from "react-router-dom";
import useRouteService from "../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../services/breadcrumbs/BreadcrumbsStorage";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { isUserOnProjects } = useRouteService();
  const currentInspectionId = urlParamsService.getInspectionId()!;

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create({
      currentSectionId: urlParamsService.getSectionId()!,
      parentEntityFromPath: {
        id: urlParamsService.getSewerObjectParentId()!,
        type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job
      }
    }, { api })
  );

  const [inspectionReportStorage] = useState(() =>
    InspectionReportStorageModel.create(
      { currentInspectionId: currentInspectionId, performDownload: true },
      { api }
    )
  );

  const [currentInspectionStorage] = useState(() =>
    CurrentInspectionStorageModel.create({ currentInspectionId }, { api })
  );

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      section: currentSectionStorage.currentSection,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
    });

    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [currentSectionStorage.currentSectionId, currentSectionStorage.currentSection, currentSectionStorage.parentEntityFromPath.id]);

  const isDownloadDisabled = useMemo(
    () =>
      !permissionsService.userHasOneOfThePermissions([
        DownloadInspectionReport,
        DownloadInspectionReportFromAssignedProject,
      ]),
    []
  );
  const getCurrentInspectionName = useCallback(
    () =>
      currentInspectionStorage.currentInspection.getName(
        currentSectionStorage.currentSection.inspectionsWithDates
      ),
    [
      currentInspectionStorage.currentInspection,
      currentSectionStorage.currentSection.inspectionsWithDates,
    ]
  );

  const getInspectionReportName = useCallback(() => {
    return `${translationService.translate("general.report")}-${
      currentSectionStorage.currentSection.name
    }-${currentInspectionStorage.currentInspection.getDate()}`;
  }, [currentInspectionStorage.currentInspection, currentSectionStorage.currentSection.name]);

  const handleDownloadReport = useCallback(() => {
    inspectionReportStorage.downloadReport(getInspectionReportName());
  }, [getInspectionReportName, inspectionReportStorage]);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([{
        entity: currentSectionStorage.parentEntityFromPath.type,
        name: await currentSectionStorage.getParentEntityName(currentSectionStorage.currentSection.project.name)
        }, {
        entity: EntityTypeFromPath.SewerObject,
        name: currentSectionStorage.currentSection.name
        }, {
        entity: EntityTypeFromPath.Inspection,
        name: getCurrentInspectionName()
        }
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentSectionStorage, currentSectionStorage.currentSection, getCurrentInspectionName, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[
        ViewInspectionReport,
        ViewInspectionReportFromAssignedProject,
      ]}
      titleKey="ViewInspectionReport.title"
      titleContext={{
        sectionName: currentSectionStorage.currentSection.name,
        inspectionName: getCurrentInspectionName(),
      }}
      menuItems={menuItems}
      isLoading={currentInspectionStorage.isLoading}
    >
      <ActionsSection>
        <ButtonsSection>
          <DownloadButton
            disabled={isDownloadDisabled || !inspectionReportStorage.report}
            onClick={handleDownloadReport}
          />
        </ButtonsSection>
      </ActionsSection>
      {!inspectionReportStorage.reportUrl && <LoadingSpinner />}
      {inspectionReportStorage.reportUrl && (
        <SinglePagePdfViewer pdfUrl={inspectionReportStorage.reportUrl} />
      )}
    </PageLayout>
  );
});
